import { useState } from 'react';
import { api, ApiError } from '../api.ts';
import { useSession } from '../session.tsx';
import { useRouter, Link } from '../router.tsx';
import { Card, Checkbox, ErrorState, Field, Loading, Tabs, Tag, useAsync, useToast } from '../ui.tsx';
import { STATUS_LABEL, STATUS_TONE, fmtDuration, fmtMoney, fmtNumber, fmtPercent } from '../format.ts';
import { VideoMediaPanel, type AssetInfo } from './VideoMedia.tsx';
import { VideoTimelinePanel, type ElementModel } from './VideoTimeline.tsx';
import { VideoEmbedPanel } from './VideoEmbed.tsx';

import type { FakeProgressConfig, MiniControlsConfig, PlayerConfig, VslStartConfig } from '../../shared/types.ts';
import { fakeProgressRatio } from '../../shared/vsl.ts';

interface VideoDetail {
  video: {
    id: string; public_id: string; title: string; description: string; status: string;
    published: 0 | 1; archived: 0 | 1; duration_sec: number | null; project_id: string | null;
    player_config: PlayerConfig; thumbnail_ref: string | null;
  };
  asset: AssetInfo | null;
  elements: ElementModel[];
  stats: {
    views: number; unique_viewers: number; plays: number; cta_shown: number; clicks: number;
    leads: number; checkouts: number; purchases: number; revenue: number;
    completion_rate: number; avg_watch_sec: number; retention_at_50: number; ctr: number; conversion_rate: number;
  };
  previewUrl: string | null;
}

export function VideoEditorPage({ videoId }: { videoId: string }): JSX.Element {
  const { workspace, canWrite } = useSession();
  const { navigate } = useRouter();
  const toast = useToast();
  const [tab, setTab] = useState('media');

  const detail = useAsync(() => api<VideoDetail>(`/api/videos/${videoId}`), [videoId, workspace?.id]);

  if (detail.loading) return <div className="page"><Loading label="Carregando o vídeo…" /></div>;
  if (detail.error || !detail.data) {
    return (
      <div className="page">
        <ErrorState message={detail.error ?? 'Vídeo não encontrado.'} onRetry={detail.reload} />
        <Link to="/app/videos" className="btn ghost small">Voltar para a biblioteca</Link>
      </div>
    );
  }

  const { video, asset, elements, stats } = detail.data;

  async function publish(): Promise<void> {
    try {
      await api(`/api/videos/${videoId}/publish`, { method: 'POST' });
      toast('Vídeo publicado. A URL pública e o embed já respondem.', 'ok');
      detail.reload();
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Falha ao publicar.';
      toast(message, 'error');
    }
  }

  async function unpublish(): Promise<void> {
    await api(`/api/videos/${videoId}/unpublish`, { method: 'POST' }).catch(() => {});
    toast('Publicação removida.', 'ok');
    detail.reload();
  }

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <Link to="/app/videos" className="card-sub">← Biblioteca</Link>
          <h1 className="page-title" style={{ marginTop: 6 }}>{video.title}</h1>
          <div className="row tight">
            <Tag tone={STATUS_TONE[video.status] ?? 'muted'}>{STATUS_LABEL[video.status] ?? video.status}</Tag>
            {video.published === 1 ? <Tag tone="ok">Publicado</Tag> : <Tag tone="muted">Não publicado</Tag>}
            <span className="card-sub">{fmtDuration(video.duration_sec)}</span>
            <span className="card-sub">· ID público {video.public_id}</span>
          </div>
        </div>
        <div className="row tight">
          {video.published === 1 && (
            <a className="btn secondary" href={`/v/${video.public_id}`} target="_blank" rel="noreferrer">Abrir player público</a>
          )}
          {video.published === 1 ? (
            <button type="button" className="btn ghost" disabled={!canWrite} onClick={unpublish}>Despublicar</button>
          ) : (
            <button type="button" className="btn" disabled={!canWrite || video.status !== 'ready'} onClick={publish}>
              Publicar
            </button>
          )}
        </div>
      </div>

      {video.status !== 'ready' && video.published === 0 && (
        <div className="alert info" style={{ marginBottom: 16 }}>
          <span>i</span>
          <div>
            A publicação é liberada apenas quando o arquivo passa na validação de container e codec.
            Status atual: <b>{STATUS_LABEL[video.status] ?? video.status}</b>.
          </div>
        </div>
      )}

      <Tabs
        active={tab}
        onChange={setTab}
        tabs={[
          { id: 'media', label: 'Mídia' },
          { id: 'timeline', label: `Timeline e CTAs (${elements.length})` },
          { id: 'player', label: 'Player' },
          { id: 'embed', label: 'Embed' },
          { id: 'stats', label: 'Métricas' },
          { id: 'info', label: 'Informações' },
        ]}
      />

      {tab === 'media' && (
        <VideoMediaPanel
          videoId={video.id}
          workspaceId={workspace?.id ?? ''}
          asset={asset}
          status={video.status}
          canWrite={canWrite}
          onChanged={detail.reload}
        />
      )}

      {tab === 'timeline' && (
        <VideoTimelinePanel
          videoId={video.id}
          elements={elements}
          duration={video.duration_sec ?? 0}
          canWrite={canWrite}
          onChanged={detail.reload}
        />
      )}

      {tab === 'player' && (
        <PlayerSettings
          videoId={video.id}
          config={video.player_config}
          publicId={video.public_id}
          published={video.published === 1}
          previewUrl={detail.data.previewUrl}
          canWrite={canWrite}
          onSaved={detail.reload}
        />
      )}

      {tab === 'embed' && (
        <VideoEmbedPanel
          publicId={video.public_id}
          dimensions={asset?.codec_report ? { width: asset.codec_report.width, height: asset.codec_report.height } : null}
          startMode={video.player_config.startMode}
          published={video.published === 1}
          aspectRatio={video.player_config.aspectRatio}
        />
      )}

      {tab === 'stats' && (
        <div className="grid" style={{ gap: 18 }}>
          <div className="grid cols-4">
            <div className="stat"><p className="stat-label">Views</p><p className="stat-value">{fmtNumber(stats.views)}</p><p className="stat-meta">{fmtNumber(stats.unique_viewers)} espectadores únicos</p></div>
            <div className="stat"><p className="stat-label">Plays</p><p className="stat-value">{fmtNumber(stats.plays)}</p><p className="stat-meta">{fmtPercent(stats.views ? stats.plays / stats.views : 0, 1)} das views</p></div>
            <div className="stat"><p className="stat-label">Retenção aos 50%</p><p className="stat-value">{fmtPercent(stats.retention_at_50, 1)}</p></div>
            <div className="stat"><p className="stat-label">Conclusão</p><p className="stat-value">{fmtPercent(stats.completion_rate, 1)}</p></div>
            <div className="stat"><p className="stat-label">CTAs exibidos</p><p className="stat-value">{fmtNumber(stats.cta_shown)}</p></div>
            <div className="stat"><p className="stat-label">Cliques</p><p className="stat-value">{fmtNumber(stats.clicks)}</p><p className="stat-meta">CTR {fmtPercent(stats.ctr, 2)}</p></div>
            <div className="stat"><p className="stat-label">Leads</p><p className="stat-value">{fmtNumber(stats.leads)}</p></div>
            <div className="stat"><p className="stat-label">Receita</p><p className="stat-value">{fmtMoney(stats.revenue)}</p><p className="stat-meta">{fmtNumber(stats.purchases)} compras</p></div>
          </div>
          <Card title="Ir para o relatório completo">
            <button type="button" className="btn" onClick={() => navigate(`/app/analytics?video=${video.id}`)}>
              Abrir analytics deste vídeo
            </button>
          </Card>
        </div>
      )}

      {tab === 'info' && (
        <VideoInfo video={video} canWrite={canWrite} onSaved={detail.reload} />
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */

function PlayerSettings({
  videoId, config, publicId, published, previewUrl, canWrite, onSaved,
}: {
  videoId: string; config: PlayerConfig; publicId: string; published: boolean;
  previewUrl: string | null; canWrite: boolean; onSaved: () => void;
}): JSX.Element {
  const toast = useToast();
  const [draft, setDraft] = useState<PlayerConfig>(config);
  const [dirty, setDirty] = useState(false);

  function update(changes: Partial<PlayerConfig>): void {
    setDraft((current) => ({ ...current, ...changes }));
    setDirty(true);
  }

  async function save(): Promise<void> {
    try {
      await api(`/api/videos/${videoId}`, { method: 'PATCH', body: { player_config: draft } });
      setDirty(false);
      toast('Configuração do player salva.', 'ok');
      onSaved();
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'Falha ao salvar.', 'error');
    }
  }

  return (
    <div className="editor-layout">
      <Card
        title="Comportamento do player"
        actions={canWrite ? <button type="button" className="btn small" disabled={!dirty} onClick={save}>Salvar</button> : undefined}
      >
        <fieldset disabled={!canWrite} style={{ border: 0, padding: 0, margin: 0 }}>
          <div className="grid cols-2">
            <Field label="Controles" id="p-controls">
              <select id="p-controls" value={draft.controls} onChange={(e) => update({ controls: e.target.value as PlayerConfig['controls'] })}>
                <option value="full">Completos</option>
                <option value="minimal">Mínimos</option>
                <option value="none">Sem controles</option>
              </select>
            </Field>
            <Field label="Proporção" id="p-ratio">
              <select id="p-ratio" value={draft.aspectRatio} onChange={(e) => update({ aspectRatio: e.target.value as PlayerConfig['aspectRatio'] })}>
                <option value="16:9">16:9 (horizontal)</option>
                <option value="9:16">9:16 (vertical)</option>
                <option value="1:1">1:1 (quadrado)</option>
                <option value="4:5">4:5 (feed)</option>
              </select>
            </Field>
            <Field label="Tema" id="p-theme">
              <select id="p-theme" value={draft.theme} onChange={(e) => update({ theme: e.target.value as 'dark' | 'light' })}>
                <option value="dark">Escuro</option>
                <option value="light">Claro</option>
              </select>
            </Field>
            <Field label="Cor de destaque" id="p-accent">
              <input id="p-accent" type="color" value={draft.accentColor} onChange={(e) => update({ accentColor: e.target.value })} />
            </Field>
          </div>

          <Checkbox checked={draft.autoplay} onChange={(v) => update({ autoplay: v })} label="Autoplay quando o navegador permitir" />
          <Checkbox checked={draft.muted} onChange={(v) => update({ muted: v })} label="Iniciar sem som" />
          <Checkbox checked={draft.smartAutoplayFallback} onChange={(v) => update({ smartAutoplayFallback: v })} label="Se o autoplay com som for bloqueado, iniciar mudo e oferecer “Ativar som”" />
          <Checkbox checked={draft.loop} onChange={(v) => update({ loop: v })} label="Repetir em loop" />
          <Checkbox checked={draft.showProgress} onChange={(v) => update({ showProgress: v })} label="Mostrar barra de progresso" />
          <Checkbox checked={draft.allowSeek} onChange={(v) => update({ allowSeek: v })} label="Permitir avançar (seek)" />
          <Checkbox checked={draft.showBigPlay} onChange={(v) => update({ showBigPlay: v })} label="Mostrar botão grande de play" />
          <Checkbox checked={draft.showReplay} onChange={(v) => update({ showReplay: v })} label="Mostrar botão de assistir de novo" />
          <Checkbox checked={draft.showVolume} onChange={(v) => update({ showVolume: v })} label="Mostrar controle de volume" />
          <Checkbox checked={draft.showFullscreen} onChange={(v) => update({ showFullscreen: v })} label="Mostrar tela cheia" />
        </fieldset>
      </Card>

      <Card title="Prévia" subtitle={published ? 'Player público real.' : 'Prévia privada com URL assinada.'}>
        <div className={`player-frame ${draft.aspectRatio === '9:16' ? 'vertical' : ''}`}>
          {published ? (
            <iframe src={`/v/${publicId}?preview=1`} title="Prévia do player" allow="autoplay; fullscreen" allowFullScreen />
          ) : previewUrl ? (
            <video src={previewUrl} controls playsInline preload="metadata" />
          ) : (
            <div style={{ display: 'grid', placeItems: 'center', height: '100%', color: 'var(--text-3)' }}>
              Envie um arquivo para ver a prévia.
            </div>
          )}
        </div>
        {!published && previewUrl && (
          <p className="card-sub" style={{ marginTop: 10 }}>
            Esta prévia usa uma URL assinada temporária gerada no backend. Ela expira em 1 hora e nunca é salva no banco.
          </p>
        )}
      </Card>

      <div className="span-all">
        <StartModeSettings draft={draft} update={update} canWrite={canWrite} dirty={dirty} onSave={save} />
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Início do vídeo: botão de play ou modo VSL                           */
/* ------------------------------------------------------------------ */

function StartModeSettings({
  draft, update, canWrite, dirty, onSave,
}: {
  draft: PlayerConfig; update: (changes: Partial<PlayerConfig>) => void;
  canWrite: boolean; dirty: boolean; onSave: () => void;
}): JSX.Element {
  const vsl = draft.vsl;
  const fake = draft.fakeProgress;
  const mini = draft.miniControls;
  const setVsl = (changes: Partial<VslStartConfig>): void => update({ vsl: { ...vsl, ...changes } });
  const setFake = (changes: Partial<FakeProgressConfig>): void => update({ fakeProgress: { ...fake, ...changes } });
  const setMini = (changes: Partial<MiniControlsConfig>): void => update({ miniControls: { ...mini, ...changes } });
  const isVsl = draft.startMode === 'vsl';

  return (
    <Card
      title="Início do vídeo"
      subtitle="Como a pessoa entra no vídeo: botão de play tradicional ou modo VSL, com prévia muda e bloco chamando o clique."
      actions={canWrite ? <button type="button" className="btn small" disabled={!dirty} onClick={onSave}>Salvar</button> : undefined}
    >
      <fieldset disabled={!canWrite} style={{ border: 0, padding: 0, margin: 0 }}>
        <div className="mode-switch" role="radiogroup" aria-label="Modo de início">
          <label className={`mode-option ${!isVsl ? 'active' : ''}`}>
            <input type="radio" name="startMode" checked={!isVsl} onChange={() => update({ startMode: 'bigplay' })} />
            <strong>Botão de play</strong>
            <span>Capa com botão grande. A pessoa clica e o vídeo começa com som.</span>
          </label>
          <label className={`mode-option ${isVsl ? 'active' : ''}`}>
            <input type="radio" name="startMode" checked={isVsl} onChange={() => update({ startMode: 'vsl' })} />
            <strong>Modo VSL</strong>
            <span>Vídeo roda mudo em prévia com um bloco pulsando. Ao clicar, volta ao início com som.</span>
          </label>
        </div>

        {isVsl && (
          <>
            <h3 className="section-title">Bloco de chamada</h3>
            <div className="vsl-preview" style={{ '--vsl-bg': vsl.color, '--vsl-fg': vsl.textColor, '--vsl-pulse': `${vsl.pulseSeconds}s` } as React.CSSProperties}>
              <div className={`vsl-preview-box ${vsl.pulseSeconds <= 0 ? 'static' : ''}`} aria-hidden="true">
                <strong>{vsl.title || ' '}</strong>
                <svg viewBox="0 0 24 24" width="44" height="44" fill="currentColor"><path d="M4 9v6h4l5 4V5L8 9H4z"/><path d="M16.5 8.5a4 4 0 0 1 0 7" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/><path d="M3 3l18 18" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"/></svg>
                <span>{vsl.subtitle || ' '}</span>
              </div>
            </div>
            <div className="grid cols-2">
              <Field label="Título" id="vsl-title">
                <input id="vsl-title" value={vsl.title} maxLength={80} onChange={(e) => setVsl({ title: e.target.value })} />
              </Field>
              <Field label="Subtítulo" id="vsl-sub">
                <input id="vsl-sub" value={vsl.subtitle} maxLength={80} onChange={(e) => setVsl({ subtitle: e.target.value })} />
              </Field>
              <Field label="Cor do bloco" id="vsl-color">
                <input id="vsl-color" type="color" value={vsl.color} onChange={(e) => setVsl({ color: e.target.value })} />
              </Field>
              <Field label="Cor do texto" id="vsl-fg">
                <input id="vsl-fg" type="color" value={vsl.textColor} onChange={(e) => setVsl({ textColor: e.target.value })} />
              </Field>
              <Field label={`Velocidade do pulso: ${vsl.pulseSeconds <= 0 ? 'sem pulso' : `${vsl.pulseSeconds.toFixed(1)}s por ciclo`}`} id="vsl-pulse" hint="Menor = pulsa mais rápido. Zero desliga.">
                <input id="vsl-pulse" type="range" min={0} max={4} step={0.1} value={vsl.pulseSeconds} onChange={(e) => setVsl({ pulseSeconds: Number(e.target.value) })} />
              </Field>
              <Field label="Ao clicar" id="vsl-restart">
                <select id="vsl-restart" value={vsl.restartOnClick ? 'restart' : 'continue'} onChange={(e) => setVsl({ restartOnClick: e.target.value === 'restart' })}>
                  <option value="restart">Voltar ao início com som</option>
                  <option value="continue">Só ligar o som, de onde está</option>
                </select>
              </Field>
            </div>

            <h3 className="section-title">Prévia enquanto não clica</h3>
            <div className="grid cols-3">
              <Field label="O que mostrar" id="vsl-preview">
                <select id="vsl-preview" value={vsl.preview} onChange={(e) => setVsl({ preview: e.target.value as VslStartConfig['preview'] })}>
                  <option value="static">Parado no primeiro frame (aparece na hora)</option>
                  <option value="segments">Alternar trechos do vídeo</option>
                  <option value="loop">Repetir do início</option>
                </select>
              </Field>
              <Field label={`Trechos: ${vsl.previewSegments}`} id="vsl-segs">
                <input id="vsl-segs" type="range" min={2} max={8} step={1} value={vsl.previewSegments} disabled={vsl.preview !== 'segments'} onChange={(e) => setVsl({ previewSegments: Number(e.target.value) })} />
              </Field>
              <Field label={`Duração de cada trecho: ${vsl.previewSegmentSec}s`} id="vsl-seglen">
                <input id="vsl-seglen" type="range" min={2} max={15} step={1} value={vsl.previewSegmentSec} disabled={vsl.preview !== 'segments'} onChange={(e) => setVsl({ previewSegmentSec: Number(e.target.value) })} />
              </Field>
            </div>
            <p className="hint">Os trechos são espalhados pelo vídeo, pulando os últimos 15% para não revelar a oferta.</p>

            <h3 className="section-title">Texto quando pausar</h3>
            <div className="grid cols-2">
              <Field label="Título" id="vsl-ptitle">
                <input id="vsl-ptitle" value={vsl.pausedTitle} maxLength={80} onChange={(e) => setVsl({ pausedTitle: e.target.value })} />
              </Field>
              <Field label="Subtítulo" id="vsl-psub">
                <input id="vsl-psub" value={vsl.pausedSubtitle} maxLength={80} onChange={(e) => setVsl({ pausedSubtitle: e.target.value })} />
              </Field>
            </div>

            <h3 className="section-title">Barra de progresso ilustrativa</h3>
            <Checkbox checked={fake.enabled} onChange={(v) => setFake({ enabled: v })} label="Mostrar barra que corre no começo e desacelera perto do fim" />
            {fake.enabled && (
              <>
                <FakeCurvePreview curve={fake.curve} color={fake.color} track={fake.trackColor} height={fake.height} />
                <div className="grid cols-3">
                  <Field label="Cor da barra" id="fake-color">
                    <input id="fake-color" type="color" value={fake.color} onChange={(e) => setFake({ color: e.target.value })} />
                  </Field>
                  <Field label={`Intensidade: ${fake.curve.toFixed(1)}`} id="fake-curve" hint="1 = linear. Quanto maior, mais corre no início.">
                    <input id="fake-curve" type="range" min={1} max={4} step={0.1} value={fake.curve} onChange={(e) => setFake({ curve: Number(e.target.value) })} />
                  </Field>
                  <Field label={`Altura: ${fake.height}px`} id="fake-h">
                    <input id="fake-h" type="range" min={2} max={10} step={1} value={fake.height} onChange={(e) => setFake({ height: Number(e.target.value) })} />
                  </Field>
                  <Field label="Posição" id="fake-pos">
                    <select id="fake-pos" value={fake.position} onChange={(e) => setFake({ position: e.target.value as FakeProgressConfig['position'] })}>
                      <option value="bottom">Embaixo</option>
                      <option value="top">Em cima</option>
                    </select>
                  </Field>
                </div>
              </>
            )}

            <h3 className="section-title">Botão pequeno de play/pause</h3>
            <Checkbox checked={mini.enabled} onChange={(v) => setMini({ enabled: v })} label="Mostrar botão discreto que só aparece com toque ou mouse sobre o vídeo" />
            {mini.enabled && (
              <div className="grid cols-3">
                <Field label="Posição" id="mini-pos">
                  <select id="mini-pos" value={mini.position} onChange={(e) => setMini({ position: e.target.value as MiniControlsConfig['position'] })}>
                    <option value="bottom-left">Canto inferior esquerdo</option>
                    <option value="bottom-right">Canto inferior direito</option>
                    <option value="top-left">Canto superior esquerdo</option>
                    <option value="top-right">Canto superior direito</option>
                  </select>
                </Field>
                <Field label={`Some após: ${(mini.hideAfterMs / 1000).toFixed(1)}s sem toque`} id="mini-hide">
                  <input id="mini-hide" type="range" min={800} max={8000} step={100} value={mini.hideAfterMs} onChange={(e) => setMini({ hideAfterMs: Number(e.target.value) })} />
                </Field>
                <Field label={`Tamanho: ${mini.size}px`} id="mini-size">
                  <input id="mini-size" type="range" min={28} max={64} step={2} value={mini.size} onChange={(e) => setMini({ size: Number(e.target.value) })} />
                </Field>
              </div>
            )}
            <p className="hint">No modo VSL a barra de controles padrão não aparece: a pessoa não avança o vídeo, só dá play, pausa e continua.</p>
          </>
        )}
      </fieldset>
    </Card>
  );
}

/** Mostra a curva da barra ilustrativa: onde a barra estaria em cada ponto real do vídeo. */
function FakeCurvePreview({ curve, color, track, height }: { curve: number; color: string; track: string; height: number }): JSX.Element {
  const marks = [0.25, 0.5, 0.75];
  return (
    <div className="fake-preview">
      <div className="fake-preview-bar" style={{ background: track, height }}>
        <div style={{ width: `${fakeProgressRatio(0.5, curve) * 100}%`, background: color, height: '100%', borderRadius: 2 }} />
      </div>
      <div className="fake-preview-marks">
        {marks.map((m) => (
          <span key={m}>{Math.round(m * 100)}% do vídeo → <strong>{Math.round(fakeProgressRatio(m, curve) * 100)}%</strong> na barra</span>
        ))}
      </div>
    </div>
  );
}

function VideoInfo({
  video, canWrite, onSaved,
}: { video: VideoDetail['video']; canWrite: boolean; onSaved: () => void }): JSX.Element {
  const toast = useToast();
  const [title, setTitle] = useState(video.title);
  const [description, setDescription] = useState(video.description);
  const [projectId, setProjectId] = useState(video.project_id ?? '');
  const projects = useAsync(() => api<{ projects: { id: string; name: string }[] }>('/api/projects'), []);

  async function save(): Promise<void> {
    try {
      await api(`/api/videos/${video.id}`, {
        method: 'PATCH',
        body: { title, description, projectId: projectId || null },
      });
      toast('Informações salvas.', 'ok');
      onSaved();
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'Falha ao salvar.', 'error');
    }
  }

  return (
    <Card title="Informações" actions={canWrite ? <button type="button" className="btn small" onClick={save}>Salvar</button> : undefined}>
      <fieldset disabled={!canWrite} style={{ border: 0, padding: 0, margin: 0 }}>
        <Field label="Título" id="v-title"><input id="v-title" value={title} onChange={(e) => setTitle(e.target.value)} /></Field>
        <Field label="Descrição" id="v-desc"><textarea id="v-desc" value={description} onChange={(e) => setDescription(e.target.value)} /></Field>
        <Field label="Projeto" id="v-proj">
          <select id="v-proj" value={projectId} onChange={(e) => setProjectId(e.target.value)}>
            <option value="">Sem projeto</option>
            {(projects.data?.projects ?? []).map((project) => (
              <option key={project.id} value={project.id}>{project.name}</option>
            ))}
          </select>
        </Field>
      </fieldset>
    </Card>
  );
}
